import React, { useState } from 'react';

import { DeleteOutlined, EditOutlined, MessageOutlined } from '@ant-design/icons';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import { formatDateTime } from '@utils/dateTime';
import { Card, Input, Button, Empty, message, Popconfirm } from 'antd';
import { useTranslation } from 'react-i18next';

export default function POComments({ comments, poId, service, currentUserId, onUpdate }) {
  const { t } = useTranslation();
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      const res = await service.createComment(poId, commentText.trim());
      if (res.success) {
        setCommentText('');
        onUpdate();
      } else {
        handleApiError(res, { t });
      }
    } catch (error) {
      handleNetworkError(error, { t });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateComment = async (commentId) => {
    if (!editText.trim()) return;
    try {
      const res = await service.updateComment(poId, commentId, editText.trim());
      if (res.success) {
        setEditingId(null);
        setEditText('');
        onUpdate();
      } else {
        handleApiError(res, { t });
      }
    } catch (error) {
      handleNetworkError(error, { t });
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const res = await service.deleteComment(poId, commentId);
      if (res.success) {
        message.success(t('common.submit.success'));
        onUpdate();
      } else {
        handleApiError(res, { t });
      }
    } catch (error) {
      handleNetworkError(error, { t });
    }
  };

  const isOwnComment = (item) => item.actor?.id === currentUserId;

  return (
    <Card
      title={<><MessageOutlined style={{ marginRight: 6 }} />{t('models.purchase_order.sidebar.timeline')} ({comments.length})</>}
      size="small"
    >
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <Input
          size="small"
          placeholder={t('models.purchase_order.sidebar.comment_placeholder')}
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          onPressEnter={handleSubmitComment}
          disabled={submitting}
        />
        <Button
          size="small"
          type="primary"
          onClick={handleSubmitComment}
          loading={submitting}
          disabled={!commentText.trim()}
        >
          {t('models.purchase_order.sidebar.submit_comment')}
        </Button>
      </div>

      {comments.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} />
      ) : (
        comments.map((item) => (
          <div key={item.id} style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0', fontSize: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <span style={{ fontWeight: 500, color: '#1677ff' }}>{item.actor?.name}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {isOwnComment(item) && editingId !== item.id && (
                  <>
                    <EditOutlined
                      style={{ color: '#888', cursor: 'pointer', fontSize: 11 }}
                      onClick={() => { setEditingId(item.id); setEditText(item.body); }}
                    />
                    <Popconfirm
                      title={t('models.purchase_order.comment.delete_confirm')}
                      onConfirm={() => handleDeleteComment(item.id)}
                      okText={t('common.button.confirm')}
                      cancelText={t('common.button.cancel')}
                    >
                      <DeleteOutlined style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: 11 }} />
                    </Popconfirm>
                  </>
                )}
                <span style={{ color: '#ccc', fontSize: 10, marginLeft: 4 }}>
                  {formatDateTime(item.created_at)}
                </span>
              </div>
            </div>
            {editingId === item.id ? (
              <div style={{ marginTop: 4, display: 'flex', gap: 4 }}>
                <Input
                  size="small"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onPressEnter={() => handleUpdateComment(item.id)}
                />
                <Button size="small" type="primary" onClick={() => handleUpdateComment(item.id)}>
                  {t('models.purchase_order.comment.save')}
                </Button>
                <Button size="small" onClick={() => setEditingId(null)}>
                  {t('models.purchase_order.comment.cancel_edit')}
                </Button>
              </div>
            ) : (
              <div style={{ marginTop: 2, color: '#333' }}>{item.body}</div>
            )}
          </div>
        ))
      )}
    </Card>
  );
}
